var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import { KeyCode } from '../../../core/utils/src/dom';
import LegacyClassBuilder from '../../../core/utils/src/class-builders/legacy';
var lastGeneratedId = 0;
export var nextGeneratedId = function () { return "awsui-tabs-" + lastGeneratedId++; };
export var Tabs = AwsUi.__addComponent('awsui-tabs', {
    releaseStatus: 'stable',
    callbacks: {
        initialized: function () {
            this.__id = nextGeneratedId();
        }
    },
    properties: {
        // Array of objects, each having the following properties:
        //
        // * `id` [string]: The tab id, this value will be set to activeTabId when the tab is selected.
        // * `label` [string/function]: Tab label shown in the UI.
        // * `content` [string/function]: Tab content to render in the container.
        // * `disabled` [boolean]: Whether this item is disabled.
        // * `href` [string]: You can use this parameter to change the default `href` of the internal tab anchor. The
        //   `click` event default behavior is prevented, unless user clicks the tab with a key modifier (CTRL,
        //   ALT, SHIFT, META). This allows to open new browser tabs with initially selected component tab,
        //   when the routing can handle such deep links. You can manually update routing on the current page
        //   using the `activeTabHref` property of `change` event detail.
        tabs: {
            type: 'array',
            itemTypes: ['Tabs.Tab'],
            onChange: function (oldTabs, newTabs) {
                this.__enabledTabs = newTabs.filter(function (tab) { return !tab.disabled; });
            }
        },
        // Id of the currently active tab.
        activeTabId: {
            type: 'string'
        },
        // The possible visual variants of tabs are the following:
        // * default: can be used in any context
        // * container: version with borders, supposed to be used along with other containers
        variant: {
            type: 'string',
            valid: ['default', 'container'],
            defaultValue: 'default'
        }
    },
    events: {
        // Fired whenever the user selects a different tab.
        // The event `detail` contains the current `activeTabId`.
        change: {
            bubbles: true,
            cancelable: false,
            detailType: 'Tabs.ChangeDetail'
        }
    },
    functions: {
        __highlightTab: function (enabledTabIndex) {
            var tab = this.__enabledTabs[enabledTabIndex];
            this.activeTabId = tab.id;
            var enabledTabNodes = this.node.querySelectorAll('.awsui-tabs-tab-link:not(.awsui-tabs-tab-disabled)');
            enabledTabNodes[enabledTabIndex].focus();
            this.__fireEvent('change', {
                activeTabId: tab.id,
                activeTabHref: tab.href
            });
        },
        __moveHighlight: function (event, tab) {
            if (event.keyCode !== KeyCode.right && event.keyCode !== KeyCode.left) {
                return;
            }
            var activeIndex = this.__enabledTabs.indexOf(tab);
            if (event.keyCode === KeyCode.right) {
                if (activeIndex + 1 === this.__enabledTabs.length) {
                    this.__highlightTab(0);
                }
                else {
                    this.__highlightTab(activeIndex + 1);
                }
            }
            else if (event.keyCode === KeyCode.left) {
                if (activeIndex === 0) {
                    this.__highlightTab(this.__enabledTabs.length - 1);
                }
                else {
                    this.__highlightTab(activeIndex - 1);
                }
            }
        }
    },
    render: function (m, h) {
        var _this = this;
        var renderTab = function (tab) {
            var clickTab = function (event) {
                if (tab.disabled || tab.id === _this.activeTabId) {
                    event.preventDefault();
                    return;
                }
                // if primary mouse button is clicked with a modifier key browser will handle opening a new tab
                var specialKey = event.button !== 0 || event.ctrlKey || event.altKey || event.shiftKey || event.metaKey;
                if (specialKey && tab.href) {
                    return;
                }
                event.preventDefault();
                _this.activeTabId = tab.id;
                _this.__fireEvent('change', {
                    activeTabId: _this.activeTabId,
                    activeTabHref: tab.href
                });
            };
            var classes = new LegacyClassBuilder(_this)
                .addComponentClass('tab-link')
                .addComponentClass('tab-active', _this.activeTabId === tab.id)
                .addComponentClass('tab-disabled', tab.disabled)
                .toClassName();
            var linkAttributes = {
                className: classes,
                'override-focus': true,
                role: 'tab',
                'aria-selected': "" + (tab.id === _this.activeTabId),
                'aria-controls': _this.__id + "-" + tab.id + "-panel"
            };
            if (tab.disabled) {
                linkAttributes['aria-disabled'] = 'true';
            }
            else {
                // Only add href attribute if tab isn't disabled.
                // This allows focus only on enabled tabs
                linkAttributes.href = tab.href || '#';
                linkAttributes.onclick = clickTab;
                linkAttributes.onkeydown = function (event) { return _this.__moveHighlight(event, tab); };
            }
            if (tab.id !== _this.activeTabId) {
                linkAttributes.tabindex = '-1';
            }
            linkAttributes['data-testid'] = tab.id;
            return (AwsUi.__createElement("li", { className: "awsui-tabs-tab", role: "presentation" },
                AwsUi.__createElement("a", __assign({}, linkAttributes),
                    AwsUi.__createElement("span", { className: "awsui-tabs-tab-label" }, _this.renderToMithril("tab-link-" + tab.id, tab.label)))));
        };
        var header = function () {
            var classes = new LegacyClassBuilder(_this)
                .addComponentClass('header')
                .addClass('awsui-util-container-header', _this.variant === 'container')
                .toClassName();
            return (AwsUi.__createElement("ul", { className: classes, role: "tablist" }, _this.tabs.map(renderTab)));
        };
        var content = function () {
            var selectedTab = _this.tabs.filter(function (tab) { return tab.id === _this.activeTabId; })[0];
            var renderContent = function (tab) {
                var isContentActive = tab === selectedTab && !selectedTab.disabled && selectedTab.content;
                var classes = new LegacyClassBuilder(_this)
                    .addComponentClass('content')
                    .addComponentClass('content-active', isContentActive)
                    .addComponentClass('content-inactive', !isContentActive)
                    .toClassName();
                var contentAttributes = {
                    className: classes,
                    role: 'tabpanel',
                    id: _this.__id + "-" + tab.id + "-panel",
                    key: _this.__id + "-" + tab.id + "-panel",
                    tabindex: '0',
                    'override-focus': true
                };
                return (AwsUi.__createElement("div", __assign({}, contentAttributes), isContentActive ? _this.renderToMithril(_this.__id + "-" + tab.id + "-panel", selectedTab.content) : null));
            };
            return _this.variant === 'container' ? (_this.tabs.map(renderContent)) : (AwsUi.__createElement("div", { className: "awsui-tabs-content-wrapper" }, _this.tabs.map(renderContent)));
        };
        var cb = new LegacyClassBuilder(this)
            .addPropertyValueClass('variant')
            .addClass('awsui-util-container', this.variant === 'container');
        return (AwsUi.__createElement("div", { className: cb.toClassName() },
            header(),
            content()));
    },
    wrapperSupport: {
        ngModel: {
            value: 'activeTabId',
            event: 'change'
        }
    }
});
//# sourceMappingURL=index.js.map